import "reflect-metadata"
import { App, createApp, h, provide, Ref, ref } from "vue"
import { AwOptions } from "../models/AwOptions"
import { addMetaTag } from "@/services/addMetaTag"
import isPreview from "@/services/isPreview"
import logger from "../logLevel"
import { WebinarDataService } from "@/services/WebinarDataServices"
import { container } from "tsyringe"
import {
  registerAnalyticsService,
  registerWebinarDataService
} from "@/tsyringe.config"
import IGlobalData, { GlobalData } from "@/models/GlobalData"
import { EventBusKey, GlobalDataKey, LoggerKey } from "@/symbols"
import eventBus from "@/event-bus"

export function createWebinarVueApp(
  widgetLayout: any,
  options: AwOptions | undefined,
  el: HTMLElement,
  shouldEnableAnalytics: boolean = true
): App<Element> {
  const globalData: Ref<IGlobalData> = ref(new GlobalData())
  const webinarAlternateId = el.getAttribute("data-w-id")
  const isWebinar = !!webinarAlternateId
  const videoid = !isWebinar ? el.getAttribute("data-v-id") : null

  if (isWebinar && options) {
    options.webinarData = null
  }

  const app = createApp({
    components: {
      widgetLayout
    },
    render() {
      return h(widgetLayout, { fetchId: webinarAlternateId ?? videoid })
    },
    created() {
      addMetaTag()
      if (options) {
        globalData.value.height = options.height ?? 0
        globalData.value.autoPlay = options.autoPlay ?? false
        if (options.apiEndpoint)
          globalData.value.baseurlAdminAppUrl = options.apiEndpoint + "/"
        if (options.dataEndpoint)
          globalData.value.PublishedWebinarDataEndPoint =
            options.dataEndpoint + "/"
        globalData.value.queryEndpoint = options.queryEndpoint ?? ""
        globalData.value.redirect_data.url = options.redirect_data?.url ?? null
        globalData.value.redirect_data.text =
          options.redirect_data?.text ?? null
        globalData.value.time_data.startTime = options.startTime ?? undefined
        globalData.value.time_data.endTime = options.endTime ?? undefined
      }
      globalData.value.analyticsEventsCaptureEndPoint = `${globalData.value.baseurlAdminAppUrl}api/v4/event`
      globalData.value.socialShareUrl = globalData.value.isLocalhost
        ? globalData.value.localbase
        : "https://snackified.co/videos/"
      setTimeout(this.focus, 2000)

      // analytics are enabled if the following conditions are true:
      // 1. the widget is NOT hosted on a product preview page
      // 2. custom webinarData was passed to the widget
      // 3. the widget is running in a non-production context
      if (shouldEnableAnalytics) {
        const shouldNotCollectAnalytics =
          !!options?.webinarData ||
          isPreview() ||
          process.env.NODE_ENV != "production"
        registerAnalyticsService(shouldNotCollectAnalytics)
      }

      // Register the dataservice to fetch data from the API if no options were passed
      if (options?.webinarData) {
        registerWebinarDataService(true)
        const dataService = container.resolve(WebinarDataService)
        dataService.setUp("undefined", "undefined", options.webinarData)
      } else {
        registerWebinarDataService(false)

        let publishedDataEndpoint =
          globalData.value.PublishedWebinarDataEndPoint
        if (!isWebinar) {
          publishedDataEndpoint += "v/"
        }

        const dataService = container.resolve(WebinarDataService)
        dataService.setUp(
          publishedDataEndpoint,
          globalData.value.queryEndpoint,
          {}
        )
      }

      //provide dependencies to be injected later
      provide(GlobalDataKey, globalData)
      provide(LoggerKey, logger)
      provide(EventBusKey, eventBus)
    }
  })
  app.mount(el!)
  return app
}
